import React from 'react';
import { Box, Container, useTheme, useMediaQuery } from '@mui/material';
import { QuestionReportCardSkeleton } from './QuestionReportCardSkeleton';

interface ReportViewerSkeletonProps {
  questionCount?: number;
  showHeader?: boolean;
}

export const ReportViewerSkeleton: React.FC<ReportViewerSkeletonProps> = ({
  questionCount = 4,
  showHeader: _showHeader = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Determine chart variants for realistic variation
  const getVariantForIndex = (index: number): 'with-chart' | 'with-comments' | 'with-both' => {
    const variants: Array<'with-chart' | 'with-comments' | 'with-both'> = [
      'with-both',
      'with-chart',
      'with-both',
      'with-comments',
    ];
    return variants[index % variants.length];
  };

  // Determine comment counts for variation
  const getCommentCountForIndex = (index: number): number => {
    return index % 2 === 0 ? 2 : 1;
  };

  // Adjust question count for mobile (show fewer skeletons)
  const displayCount = isMobile && questionCount > 3 ? 3 : questionCount;

  return (
    <Box role="status" aria-live="polite" aria-label="Loading report">
      {/* Questions Section */}
      <Container maxWidth={false} disableGutters>
        <Box>
          {Array.from({ length: displayCount }, (_, index) => (
            <Box
              key={index}
              sx={{
                opacity: Math.max(0.3, 1 - index * 0.15), // Staggered opacity
                animationDelay: `${index * 100}ms`, // Staggered animation delay
              }}
            >
              <QuestionReportCardSkeleton
                variant={getVariantForIndex(index)}
                chartSize={isMobile ? 'small' : 'large'}
                commentCount={getCommentCountForIndex(index)}
                compact={false}
                animate={index < 2} // Only first 2 animate for performance
                isMobile={isMobile}
              />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
